import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useDebouncedPropertyFetch from "./useDebouncedPropertyFetch";

const defaultGetOptionValue = (option) => option?.value;

/**
 * Server-side searchable property options for a multi-select TagPicker.
 *
 * Fetches one page (limit 20) per search keyword and keeps previously seen and
 * currently selected options available, so their tags keep rendering even when
 * they fall outside the latest search results.
 *
 * Consumers should pass stable (useCallback / useMemo) values for `toOption`,
 * `filterProperty` and `getOptionValue` to avoid unnecessary refetching.
 *
 * @param {object} params
 * @param {Array} params.selectedValues - currently selected option values
 * @param {(property: object) => (object|null)} params.toOption - maps a raw
 *   property to a picker option (e.g. the raw property, or `{ label, value }`)
 * @param {(property: object) => boolean} [params.filterProperty] - optional
 *   predicate to include/exclude a property (e.g. pilot vs non-pilot)
 * @param {Array} [params.seedProperties] - known properties used to seed the
 *   cache (e.g. the context list or persisted selections)
 * @param {(option: object) => *} [params.getOptionValue] - reads an option's
 *   value (defaults to `option.value`)
 * @returns {{ options: Array, loading: boolean, onSearch: (keyword: string) => void }}
 */
const usePropertyPickerOptions = ({
  selectedValues = [],
  toOption,
  filterProperty,
  seedProperties,
  getOptionValue = defaultGetOptionValue,
}) => {
  const [results, setResults] = useState([]);
  // value -> option: accumulates every option we've shown or seeded so a
  // selected value can always be resolved back to a labelled option.
  const cacheRef = useRef(new Map());

  const { loading, fetchProperties, debouncedFetch } =
    useDebouncedPropertyFetch((data) => setResults(data?.properties ?? []));

  // Initial page load.
  useEffect(() => {
    fetchProperties("");
  }, [fetchProperties]);

  const onSearch = debouncedFetch;

  // Reset the options back to the default first page (e.g. after a selection),
  // mirroring the client-side behaviour where selecting cleared the search.
  const reset = useCallback(() => fetchProperties(""), [fetchProperties]);

  // Seed the cache with already-known properties (context list, persisted
  // selections) so their tags render before the user searches.
  //
  // Seeded unfiltered on purpose. The cache is only consulted for values that
  // are already selected, so filtering it cannot put an unselected property
  // within reach — it only decides whether an *already selected* one resolves to
  // a labelled option or renders as a bare id. An edited promo may legitimately
  // hold a property the current filter excludes; showing its name (as its tag,
  // and as its own already-ticked row) beats showing an unlabelled one.
  useEffect(() => {
    (seedProperties || []).forEach((property) => {
      const option = toOption(property);
      if (option) cacheRef.current.set(getOptionValue(option), option);
    });
  }, [seedProperties, toOption, getOptionValue]);

  // Map the current search results to options and remember them.
  const resultOptions = useMemo(
    () =>
      results
        .filter((property) =>
          filterProperty ? filterProperty(property) : true
        )
        .map((property) => toOption(property))
        .filter(Boolean),
    [results, toOption, filterProperty]
  );

  useEffect(() => {
    resultOptions.forEach((option) =>
      cacheRef.current.set(getOptionValue(option), option)
    );
  }, [resultOptions, getOptionValue]);

  // Final data = current results + any selected options not in the results
  // (pulled from the cache) so selected tags always resolve to a label.
  const options = useMemo(() => {
    const byValue = new Map();
    resultOptions.forEach((option) =>
      byValue.set(getOptionValue(option), option)
    );
    selectedValues.forEach((value) => {
      if (!byValue.has(value) && cacheRef.current.has(value)) {
        byValue.set(value, cacheRef.current.get(value));
      }
    });
    return Array.from(byValue.values());
  }, [resultOptions, selectedValues, getOptionValue]);

  return { options, loading, onSearch, reset };
};

export default usePropertyPickerOptions;
